/**
 * The pure half of a voice session's timers: which deadline is next, and what
 * the user is told when it fires.
 *
 * Before this module there were five independent `setTimeout`s in
 * `voiceSession.js`. Four of them ended the session with the same
 * `stop("idle")`, the fifth with `stop("timeout")`, and none of them reached
 * the screen. A person whose session vanished could not tell whether they had
 * been quiet for a minute, had never been heard at all, or had simply read a
 * delete card too slowly — three different problems with three different
 * answers, all reported as nothing.
 *
 * So the timers become ONE SET with named kinds. The set is pure: it holds
 * absolute deadlines and answers questions about them. It owns no `setTimeout`,
 * reads no clock of its own, and touches no browser global — every entry point
 * takes `now`, which is what makes the whole thing testable with a fake clock
 * and what keeps a session's scheduling in the one place that also owns the
 * microphone. The session keeps a SINGLE `setTimeout` aimed at `next()`, and
 * re-aims it whenever anything is armed or disarmed.
 *
 * Earliest deadline wins. That is the entire scheduling policy, and it is why
 * the 90 s ceiling cannot be pushed out by a 60 s silence timer re-arming under
 * it: the ceiling is armed once, at start, on an absolute deadline.
 */

/**
 * Order matters twice: it is the tie-break when two deadlines land on the same
 * millisecond, and it is the order a reader should think about them in —
 * "was anything heard", "was the question answered", "was the card decided",
 * then the two session-wide backstops.
 */
export const TIMER_KINDS = [
  "no_speech",
  "answer",
  "confirm",
  "silence",
  "max_session",
];

/**
 * The deadline that belongs to the STREAM rather than to the session.
 *
 * Deliberately not in `TIMER_KINDS`, and the separation is the point of it.
 * Every kind above ends the session: it has a "Stopped: …" line, a stop reason
 * the modal understands, and a user who has to be told which window ran out.
 * `stream_idle` ends only the paid half. The microphone graph keeps running,
 * the card keeps its hash and its key, the confirm window keeps counting, and
 * the panel goes back to Ready with nothing to explain — so it has no exit copy
 * and no stop reason ON PURPOSE. `exitCopy("stream_idle")` answers `''`, which
 * is what item 3 of the packet asks for, and it answers it because the kind is
 * absent from `EXIT_COPY` rather than because of a special case.
 *
 * It shares the one timer set (and therefore the session's one `setTimeout`)
 * because "earliest deadline wins" has to include it: a stream that is about to
 * be closed and a session that is about to end are two answers to the same
 * question, and two schedulers would be free to disagree about which came
 * first.
 */
export const STREAM_TIMER_KINDS = ["stream_idle"];

/**
 * The numbers `voiceSession.js` has always used, in one table. Each is
 * explained where it is armed; the reasoning has not changed, only the place
 * the value is written down.
 */
export const TIMER_DEFAULTS_MS = {
  no_speech: 20000,
  answer: 15000,
  confirm: 12000,
  silence: 60000,
  max_session: 90000,
};

/**
 * What a closed session says, per timer.
 *
 * Every line names the thing that ran out, because "the session ended" is not
 * information. The confirm line also says that NOTHING WAS CHANGED — a window
 * closing over a delete card is exactly the moment a person needs to be told
 * that their inventory is untouched.
 */
export const EXIT_COPY = {
  no_speech: "Stopped: no speech for 20 s",
  answer: "Stopped: no answer in 15 s",
  confirm: "Stopped: {s} s passed without a confirm. Nothing was changed.",
  silence: "Stopped: quiet for 60 s",
  max_session: "Stopped: 90 s session limit",
};

/**
 * The reason strings the modal already understands, unchanged. Only the 90 s
 * ceiling is a `timeout`; everything else is the session going `idle`. Adding a
 * new reason string here would mean teaching every consumer a new word, and the
 * kind — which is new, and carried alongside — is the thing that says which.
 */
export const STOP_REASON_FOR_KIND = {
  no_speech: "idle",
  answer: "idle",
  confirm: "idle",
  silence: "idle",
  max_session: "timeout",
};

/**
 * Reading a rendered card with no spoken read-back takes longer than hearing
 * one. With replies off the window used to start at render and still run the
 * spoken-length 12 s, so someone reading a delete card in gloves lost the
 * session mid-sentence. This floor is the read-time allowance.
 */
const CONFIRM_READ_FLOOR_MS = 20000;

/**
 * A positive number from the build's environment, or the fallback.
 *
 * Wrapped rather than read inline because this module is loaded by a node test
 * with a real `process` and by a browser bundle where `process.env` is whatever
 * the bundler substituted. Neither is allowed to throw here: a missing or
 * nonsense value means "use the default", never "no timer".
 */
function envNumber(name, fallback) {
  try {
    const env =
      typeof process !== "undefined" && process && process.env
        ? process.env
        : null;
    const value = Number(env ? env[name] : null);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * How long a stream with nothing happening on it stays open.
 *
 * The microphone is free to hold; the STREAM is what Muse bills, by the audio
 * minute, silence included. Eight seconds is long enough to cover the pause
 * between "move sample beta to VP14" and the correction that follows it, and
 * short enough that a panel left open on a bench costs nothing after the first
 * one. It is a DEFAULT: `VUE_APP_VOICE_STREAM_IDLE_MS` moves it without a code
 * change, which is what makes it tunable against a real lab rather than against
 * an argument.
 */
export const VOICE_STREAM_IDLE_MS = envNumber(
  "VUE_APP_VOICE_STREAM_IDLE_MS",
  8000
);

/**
 * Whether an idle stream may actually be closed, or whether something is still
 * owed to the person looking at the panel.
 *
 * Four things hold it open, and each is a case where closing would break a
 * promise the dock has already made on screen:
 *
 *  - a TURN IS IN FLIGHT. A completed transcript has gone up and its parse has
 *    not come back, or the dock's own preview round trip is running. The
 *    server's `voice:stop` handler CANCELS an outstanding parse and never emits
 *    the proposal (`voice-relay.test.mjs`, "cuts immediately on voice:stop and
 *    never emits the late proposal") — so closing here would not merely cost a
 *    read-back, it would silently lose the command the user just spoke. The
 *    caller bounds this one on time, because a wait that never ends is a stream
 *    that never closes.
 *  - a read-back is PLAYING. The machine is mid-sentence; the confirm window
 *    opens when it ends, and a stream torn down under it would take the spoken
 *    decision that sentence is asking for with it.
 *  - a card is pending WITH SPOKEN REPLIES ON. The user has just been told
 *    "Say confirm, or tap the button", so the word has to be able to land. This
 *    covers the gap between the card rendering and the read-back starting as
 *    well as the window itself, because in that gap the promise is already on
 *    screen.
 *  - an answer window is armed. The model asked a question and the panel says
 *    "Answer it"; the answer needs a stream to arrive on.
 *
 * Everything else — an idle panel, a card with replies OFF, a session resting
 * between commands — may close. With replies off nothing has offered a spoken
 * confirm, the card's own copy says to tap instead, and the tap that re-opens
 * the stream is the same tap that would have been needed to speak anyway.
 *
 * @param {{turnInFlight?: boolean, playing?: boolean, confirmPending?: boolean,
 *   repliesOn?: boolean, answerArmed?: boolean}} [state]
 * @returns {boolean} true when the stream may be torn down now
 */
export function streamIdleAllowed({
  turnInFlight = false,
  playing = false,
  confirmPending = false,
  repliesOn = true,
  answerArmed = false,
} = {}) {
  if (turnInFlight) return false;
  if (playing) return false;
  if (confirmPending && repliesOn !== false) return false;
  if (answerArmed) return false;
  return true;
}

/**
 * Position in the kind order, for the same-millisecond tie-break.
 *
 * `stream_idle` comes FIRST, so a tie closes the stream rather than ending the
 * session. That is the gentler of the two outcomes — the card, the mic and the
 * user's place in the conversation all survive it — and a tie-break has to pick
 * one deterministically.
 */
const KIND_ORDER = [...STREAM_TIMER_KINDS, ...TIMER_KINDS].reduce(
  (order, kind, index) => {
    order[kind] = index;
    return order;
  },
  Object.create(null)
);

const isKind = (kind) =>
  Object.prototype.hasOwnProperty.call(KIND_ORDER, kind);

function requireNow(now, where) {
  if (!Number.isFinite(now)) {
    throw new TypeError(`voiceTimers.${where} needs a numeric \`now\``);
  }
  return now;
}

/**
 * The sentence for a closed session, with the confirm window's real length in
 * it — which is not always 12 s, because `confirmWindowMs` widens it when
 * spoken replies are off, and telling someone "12 s passed" after 20 s did is
 * a small lie that makes the rest of the copy less trustworthy.
 *
 * An unknown kind returns `''` rather than throwing: this is called on the way
 * out of a session, from a render path, and a missing line is a better failure
 * there than a blank dock.
 *
 * @param {string} kind one of `TIMER_KINDS`
 * @param {number} [totalMs] the window that actually ran; defaults to the kind's
 *   own default, so a caller that has lost the number still gets true copy.
 * @returns {string}
 */
export function exitCopy(kind, totalMs) {
  if (!Object.prototype.hasOwnProperty.call(EXIT_COPY, kind)) return "";
  const template = EXIT_COPY[kind];
  const ms = Number.isFinite(totalMs) ? totalMs : TIMER_DEFAULTS_MS[kind];
  return template.replace("{s}", String(Math.round(ms / 1000)));
}

/**
 * How long a rendered proposal keeps the session open.
 *
 * With replies on, the window starts when the read-back finishes and 12 s is a
 * spoken decision's worth of time. With replies off it starts at render and the
 * person is reading, so it gets the read-time floor instead.
 *
 * @param {object} [options]
 * @param {boolean} [options.repliesOn=true]
 * @returns {number} milliseconds
 */
export function confirmWindowMs({ repliesOn = true } = {}) {
  return repliesOn
    ? TIMER_DEFAULTS_MS.confirm
    : Math.max(TIMER_DEFAULTS_MS.confirm, CONFIRM_READ_FLOOR_MS);
}

/**
 * A set of absolute deadlines, keyed by kind.
 *
 * @returns {{
 *   arm: (kind: string, totalMs: number, now: number) => object,
 *   disarm: (kind: string) => boolean,
 *   clear: () => void,
 *   next: (now: number) => object|null,
 *   expired: (now: number) => string|null,
 *   snapshot: () => object,
 * }}
 */
export function createTimerSet() {
  /** kind -> { deadlineAt, totalMs, armedAt } */
  const deadlines = new Map();

  /**
   * Arm or re-arm one kind. Re-arming MOVES the deadline rather than adding a
   * second one, which is what makes `armSilenceTimer` on every transcript safe
   * to call as often as the relay wants to.
   *
   * Throws on an unknown kind or a non-finite duration. Both are programming
   * errors on a path where the alternative — a silently unarmed backstop — is a
   * live microphone with nothing watching it.
   */
  function arm(kind, totalMs, now) {
    if (!isKind(kind)) {
      throw new TypeError(`voiceTimers: unknown timer kind "${kind}"`);
    }
    if (!Number.isFinite(totalMs) || totalMs < 0) {
      throw new RangeError(
        `voiceTimers: ${kind} needs a non-negative duration, got ${totalMs}`
      );
    }
    requireNow(now, "arm");
    const entry = { deadlineAt: now + totalMs, totalMs, armedAt: now };
    deadlines.set(kind, entry);
    return { kind, deadlineAt: entry.deadlineAt, totalMs: entry.totalMs };
  }

  /** Remove one kind. A no-op — and `false` — if it was not armed. */
  function disarm(kind) {
    return deadlines.delete(kind);
  }

  /** Remove every kind. Used by `stop()`, where nothing is coming back. */
  function clear() {
    deadlines.clear();
  }

  /**
   * The deadline that will fire first, or `null` when nothing is armed. Ties
   * break on the kind order so the answer is deterministic; without that, two
   * timers armed in the same millisecond would report whichever the Map
   * happened to hold first.
   *
   * `exclude` answers the SECOND question a session asks this set: not "when do
   * I wake up" but "what do I tell the user". The stream's idle deadline is
   * invisible — it ends no session, writes no exit copy, and is pushed out again
   * by every word spoken — so a countdown ring drawn on it would reset itself
   * under a card whose own 12 s window is the thing the person is racing. The
   * session schedules on every kind and reports on the ones that mean
   * something.
   *
   * @param {number} now
   * @param {{exclude?: string[]}} [options]
   */
  function next(now, { exclude } = {}) {
    requireNow(now, "next");
    const skip = Array.isArray(exclude) ? exclude : null;
    let best = null;
    deadlines.forEach((entry, kind) => {
      if (skip && skip.indexOf(kind) !== -1) return;
      if (
        best === null ||
        entry.deadlineAt < best.deadlineAt ||
        (entry.deadlineAt === best.deadlineAt &&
          KIND_ORDER[kind] < KIND_ORDER[best.kind])
      ) {
        best = { kind, deadlineAt: entry.deadlineAt, totalMs: entry.totalMs };
      }
    });
    if (best === null) return null;
    return {
      kind: best.kind,
      deadlineAt: best.deadlineAt,
      totalMs: best.totalMs,
      remainingMs: Math.max(0, best.deadlineAt - now),
    };
  }

  /**
   * The kind that has run out, if any. Inclusive at the boundary: a deadline of
   * exactly `now` has expired, because the session's one `setTimeout` is aimed
   * at that instant and a strict comparison there would reschedule a zero-delay
   * timeout forever.
   */
  function expired(now) {
    requireNow(now, "expired");
    const soonest = next(now);
    if (soonest === null) return null;
    return soonest.deadlineAt <= now ? soonest.kind : null;
  }

  /** `{ kind: deadlineAt }` for what is armed. Diagnostics and tests only. */
  function snapshot() {
    const out = {};
    deadlines.forEach((entry, kind) => {
      out[kind] = entry.deadlineAt;
    });
    return out;
  }

  return { arm, disarm, clear, next, expired, snapshot };
}

/**
 * The delay a session should hand to `setTimeout`, or `null` when there is
 * nothing to schedule.
 *
 * Clamped at 0 because a deadline can already be in the past by the time this
 * is asked — a tab that was backgrounded, a long synchronous block, a clock
 * that moved — and `setTimeout(fn, -400)` is a silent lie that fires
 * immediately anyway. Making the clamp explicit and testable is the point of
 * pulling it out of the session.
 *
 * @param {{next: Function}} set a timer set from `createTimerSet`
 * @param {number} now
 * @returns {number|null}
 */
export function delayUntilNext(set, now) {
  requireNow(now, "delayUntilNext");
  const soonest = set.next(now);
  if (soonest === null) return null;
  return Math.max(0, soonest.deadlineAt - now);
}
